import React from 'react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faArrowUpRightFromSquare,
    faClock,
    faFileLines,
    faTrash,
    faVideo,
} from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';

import {CalendarEntryDto, CalendarTaskDto, OutlookEventDto} from 'apiClient/Api';

import {CalendarEventDetail, isWritableEntry} from './calendarSources';

interface Props {
    detail: CalendarEventDetail | null;
    onClose: () => void;
    /**
     * Gehört der Termin zum **eigenen** Postfach?
     *
     * Steuert den Knopf „In Outlook öffnen": der `webLink` von Graph zeigt auf den Termin **im
     * Postfach seines Besitzers**. Wer ihn für einen fremden Kalender anklickt, landet in seinem
     * eigenen Outlook auf einer Fehlermeldung — der Knopf verspricht dort etwas, das er nicht
     * halten kann. Vorgabe `true`, weil der Regelfall der eigene Kalender ist.
     */
    ownMailbox?: boolean;
    /**
     * Öffnet die Zeitbuchung für die Auftragsnummer eines Labelwin-Eintrags.
     *
     * Nur setzen, wenn der Benutzer buchen darf (`labelwin.time.book`) — die aufrufende Seite
     * kennt die Berechtigung und stellt auch den Buchungs-Dialog. Ohne Callback erscheint der
     * Knopf nicht.
     */
    onBookTime?: (orderNumber: string) => void;
    /**
     * Löscht den angezeigten Labelwin-Eintrag.
     *
     * Nur setzen, wenn im eigenen Kalender geschrieben werden darf. Der Knopf erscheint zusätzlich
     * nur bei Einträgen, die der Server auch löschen lässt (freier Termin oder einzelner
     * Abwesenheitstag ohne Auftragsbezug) — sonst verspräche er etwas, das im 409 endet.
     */
    onDelete?: (entryId: number) => void;
    /**
     * Öffnet den Auftrag hinter dem Termin.
     *
     * Dort wird terminiert, Zeit gebucht und abgeschlossen — aus dem Kalender heraus ist das der
     * Weg vom Termin in die Arbeit am Fall.
     */
    onOpenOrder?: (orderNumber: string) => void;
}

/** Eine beschriftete Zeile. Fehlt der Wert, entfällt die Zeile ganz. */
const Row: React.FC<{label: string; children?: React.ReactNode}> = ({label, children}) => {
    if (children === null || children === undefined || children === '') return null;
    return (
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 0, sm: 1.5}}>
            <Typography variant="body2" color="text.secondary"
                        sx={{minWidth: 150, flexShrink: 0}}>
                {label}
            </Typography>
            <Typography variant="body2" component="div" sx={{minWidth: 0, wordBreak: 'break-word'}}>
                {children}
            </Typography>
        </Stack>
    );
};

/** Ein Freitext-Absatz mit Überschrift; erhält die Zeilenumbrüche der Quelle. */
const Paragraph: React.FC<{label: string; text?: string | null}> = ({label, text}) => {
    if (!text || text.trim().length === 0) return null;
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{mb: 0.25}}>{label}</Typography>
            <Typography variant="body2" sx={{whiteSpace: 'pre-line'}}>{text.trim()}</Typography>
        </Box>
    );
};

/** Zeitraum eines Termins in Langform. */
const period = (
    start?: string | null,
    end?: string | null,
    allDay?: boolean,
    locale?: string,
): string => {
    if (!start) return '—';
    const from = dayjs(start);
    const to = end ? dayjs(end) : null;
    const day = from.toDate().toLocaleDateString(locale, {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    });

    if (allDay) return day;
    const time = (value: typeof from) => value.format('HH:mm');
    return to && to.isSame(from, 'day')
        ? `${day}, ${time(from)} – ${time(to)}`
        : to
            ? `${day}, ${time(from)} – ${to.format('DD.MM.YYYY HH:mm')}`
            : `${day}, ${time(from)}`;
};

/**
 * Detail-Dialog eines Termins.
 *
 * Bewusst eigener Dialog statt des eingebauten: der zeigt die Beschreibung als einen Block
 * einfachen Text. Anklickbare Telefonnummern, ein Knopf zur Teams-Besprechung und ein Link nach
 * Outlook sind damit nicht möglich — und genau das braucht man, wenn man aus dem Kalender heraus
 * arbeiten will.
 */
export const EventDetailDialog: React.FC<Props> = (
    {detail, onClose, ownMailbox = true, onBookTime, onDelete, onOpenOrder},
) => {
    const {t, i18n} = useTranslation();
    const locale = i18n.language;

    const title = detail === null
        ? ''
        : detail.kind === 'outlook'
            ? detail.event.subject?.trim() || t('calendar.noSubject')
            : detail.kind === 'entry'
                ? detail.entry.subject?.trim() || '—'
                : detail.task.subject?.trim() || '—';

    return (
        <Dialog open={detail !== null} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{pb: 1}}>{title}</DialogTitle>
            <DialogContent dividers>
                {detail?.kind === 'entry' && <EntryDetails entry={detail.entry} locale={locale}/>}
                {detail?.kind === 'task' && <TaskDetails task={detail.task} locale={locale}/>}
                {detail?.kind === 'outlook' && <OutlookDetails event={detail.event} locale={locale}/>}
            </DialogContent>
            <DialogActions>
                {detail?.kind === 'entry' && onDelete && detail.entry.id != null
                    && isWritableEntry(detail.entry) && (
                    <Button color="error" startIcon={<FontAwesomeIcon icon={faTrash}/>}
                            onClick={() => onDelete(detail.entry.id!)}>
                        {t('calendar.delete')}
                    </Button>
                )}
                {detail?.kind === 'entry' && detail.entry.orderNumber && onOpenOrder && (
                    <Button startIcon={<FontAwesomeIcon icon={faFileLines}/>}
                            onClick={() => onOpenOrder(detail.entry.orderNumber!)}>
                        {t('orders.open')}
                    </Button>
                )}
                {detail?.kind === 'entry' && detail.entry.orderNumber && onBookTime && (
                    <Button startIcon={<FontAwesomeIcon icon={faClock}/>}
                            onClick={() => onBookTime(detail.entry.orderNumber!)}>
                        {t('timeTracking.book')}
                    </Button>
                )}
                {detail?.kind === 'outlook' && detail.event.webLink && ownMailbox && (
                    <Button component="a" href={detail.event.webLink} target="_blank" rel="noreferrer"
                            startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare}/>}>
                        {t('calendar.openInOutlook')}
                    </Button>
                )}
                <Button variant="contained" onClick={onClose}>{t('calendar.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

/** Ein Labelwin-Plantafel-Eintrag mit dem Auftrag dahinter. */
const EntryDetails: React.FC<{entry: CalendarEntryDto; locale: string}> = ({entry, locale}) => {
    const {t} = useTranslation();
    const order = entry.order;

    return (
        <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                <Chip size="small" label={t('calendar.sources.labelwin')} color="primary" variant="outlined"/>
                {entry.kind && <Chip size="small" variant="outlined" label={t(`calendar.sources.${kindKey(entry.kind)}`)}/>}
            </Stack>

            <Typography variant="body2">
                {period(entry.start, entry.end, entry.isAllDay, locale)}
            </Typography>

            <Divider/>

            <Row label={t('calendar.field.order')}>{order?.number ?? entry.orderNumber}</Row>
            <Row label={t('calendar.field.orderSubject')}>{order?.subject}</Row>
            <Row label={t('calendar.field.customer')}>
                {[order?.customerName, order?.customerCity].filter(Boolean).join(', ') || undefined}
            </Row>
            <Row label={t('calendar.field.system')}>{order?.systemName}</Row>
            <Row label={t('calendar.field.supportId')}>{order?.supportId}</Row>
            <Row label={t('calendar.field.contract')}>{order?.contractNumber}</Row>
            <Row label={t('calendar.field.contact')}>
                <Contact name={order?.contactName} phone={order?.contactPhone} mail={order?.contactMail}/>
            </Row>
            <Row label={t('calendar.field.channel')}>{order?.channel}</Row>
            <Row label={t('calendar.field.priority')}>{order?.priority}</Row>
            <Row label={t('calendar.field.project')}>{entry.project}</Row>
            <Row label={t('calendar.field.hours')}>
                {[entry.hours, order?.plannedHours && `${t('calendar.field.plannedHours')} ${order.plannedHours}`]
                    .filter(Boolean).join(' · ') || undefined}
            </Row>

            <Paragraph label={t('calendar.field.note')} text={entry.note}/>
            <Paragraph label={t('calendar.field.problem')} text={order?.comment}/>
            {entry.tooltip && entry.tooltip !== order?.subject && entry.tooltip !== entry.note && (
                <Paragraph label={t('calendar.field.tooltip')} text={entry.tooltip}/>
            )}
        </Stack>
    );
};

/** Eine Wiedervorlage. */
const TaskDetails: React.FC<{task: CalendarTaskDto; locale: string}> = ({task, locale}) => {
    const {t} = useTranslation();

    return (
        <Stack spacing={1.25}>
            <Chip size="small" variant="outlined" label={t('calendar.sources.task')}/>
            <Typography variant="body2">
                {period(task.start ?? `${task.date}T00:00:00`, task.end, !task.start, locale)}
            </Typography>
            <Divider/>
            <Row label={t('calendar.field.activity')}>{task.activity}</Row>
            <Row label={t('calendar.field.status')}>{task.status}</Row>
            <Row label={t('calendar.field.createdBy')}>{task.createdBy}</Row>
            <Row label={t('calendar.field.priority')}>{task.priority}</Row>
        </Stack>
    );
};

/** Ein Outlook-Termin — mit Knopf in die Teams-Besprechung. */
const OutlookDetails: React.FC<{event: OutlookEventDto; locale: string}> = ({event, locale}) => {
    const {t} = useTranslation();

    return (
        <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                <Chip size="small" label={t('calendar.sources.o365')} color="info" variant="outlined"/>
                {event.showAs && (
                    <Chip size="small" variant="outlined" label={t(`calendar.showAs.${event.showAs}`)}/>
                )}
                {event.isCancelled && (
                    <Chip size="small" color="error" label={t('calendar.cancelled')}/>
                )}
            </Stack>

            <Typography variant="body2">
                {period(event.start, event.end, event.isAllDay, locale)}
            </Typography>

            {/*
              * Der Beitrittslink ist das, was man im Termin tatsächlich braucht — deshalb ein Knopf
              * ganz oben statt einer 300 Zeichen langen URL irgendwo im Text.
              */}
            {event.isOnlineMeeting && event.onlineMeetingUrl && (
                <Box>
                    <Button variant="contained" size="small"
                            component="a" href={event.onlineMeetingUrl}
                            target="_blank" rel="noreferrer"
                            startIcon={<FontAwesomeIcon icon={faVideo}/>}>
                        {t('calendar.joinMeeting')}
                    </Button>
                </Box>
            )}

            <Divider/>

            <Row label={t('calendar.field.location')}>{event.location}</Row>
            <Row label={t('calendar.field.organizer')}>
                <Contact name={event.organizer} mail={event.organizerMail}/>
            </Row>
            <Row label={t('calendar.field.attendees')}>{event.attendeeCount || undefined}</Row>
            <Row label={t('calendar.field.response')}>
                {event.responseStatus ? t(`calendar.response.${event.responseStatus}`) : undefined}
            </Row>

            <Paragraph label={t('calendar.field.preview')} text={event.preview}/>
        </Stack>
    );
};

/** Ansprechpartner mit anklickbarer Nummer und Adresse. */
const Contact: React.FC<{name?: string | null; phone?: string | null; mail?: string | null}> =
    ({name, phone, mail}) => {
        if (!name && !phone && !mail) return null;

        return (
            <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                {name && <span>{name}</span>}
                {phone && (
                    // tel: statt Text — auf dem Telefon wählt das direkt, am Rechner übernimmt es
                    // die Softphone-Anwendung.
                    <Link href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</Link>
                )}
                {mail && <Link href={`mailto:${mail}`}>{mail}</Link>}
            </Stack>
        );
    };

/** Übersetzungsschlüssel der Kategorie. */
const kindKey = (kind: string): string => {
    switch (kind) {
        case 'Order':
            return 'order';
        case 'Appointment':
            return 'appointment';
        case 'Absence':
            return 'absence';
        case 'Project':
            return 'project';
        case 'Task':
            return 'task';
        default:
            return 'other';
    }
};

export default EventDetailDialog;
